import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import api from '../utils/api';
import { Alert, Button } from './site/ui';
import { IconLock } from './icons';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// Payment form component (caseId optional: when absent, creates intent without case for deferred flow)
// postcode: optional; UK postcodes contain letters — we use our own field and hide Stripe's (which can be numeric-only)
const PaymentForm = ({ caseId, phoneNumber, amount, postcode, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const processPayment = async () => {
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const endpoint = caseId ? '/api/stripe/sms-payment' : '/api/stripe/sms-payment-intent';
      const payload = caseId ? { caseId, phoneNumber } : { phoneNumber };
      const { data } = await api.post(endpoint, payload);

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        data.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              phone: phoneNumber,
              ...(postcode && { address: { postal_code: postcode } })
            }
          }
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
        if (onError) onError(confirmError);
      } else if (paymentIntent?.status === 'succeeded') {
        if (onSuccess) onSuccess(paymentIntent);
        setProcessing(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed');
      setProcessing(false);
      if (onError) onError(err);
    }
  };

  const cardElementOptions = {
    hidePostalCode: true, // Use our own postcode field — Stripe's can be numeric-only; UK postcodes need letters
    style: {
      base: {
        fontSize: '16px',
        color: '#0F1A17',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#8A9A93',
        },
      },
      invalid: {
        color: '#B42318',
      },
    },
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-ink-300 bg-white p-4 transition-colors focus-within:border-brand-600 focus-within:ring-4 focus-within:ring-brand-600/10">
        <label className="mb-2.5 block text-sm font-600 text-ink-800">Card details</label>
        <CardElement options={cardElementOptions} />
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <Button
        disabled={!stripe || processing}
        loading={processing}
        onClick={processPayment}
        size="lg"
        className="w-full"
      >
        {processing ? 'Processing…' : `Pay £${amount.toFixed(2)}`}
      </Button>

      <p className="flex items-center justify-center gap-1.5 text-xs text-ink-500">
        <IconLock size={13} />
        Payments are processed securely by Stripe.
      </p>
    </div>
  );
};

// Main Stripe payment component (caseId optional for deferred flow: pay first, then create case)
const StripePayment = ({ caseId, phoneNumber, amount, postcode, onSuccess, onError }) => {
  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    return (
      <Alert tone="warning" title="Stripe is not configured.">
        Payment processing is disabled.
      </Alert>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ locale: 'en-GB' }}>
      <PaymentForm
        caseId={caseId ?? undefined}
        phoneNumber={phoneNumber}
        amount={amount}
        postcode={postcode}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

export default StripePayment;
